import Config from 'Config';
import { detectProduct, getProductDisplayName, getProductName } from 'helpers/product';
import { OrganizationGraphQLResponse } from 'hooks/use-organizations';
import Location from 'services/Location';
import { AccountRole } from 'services/connectivity/accounts/types';
import { Product } from 'services/connectivity/products/types';
import { Product as ProductCookie } from 'types/product';

interface IGenerateAvatarHeroTextProps {
  organizations: OrganizationGraphQLResponse[];
  accountRoles: AccountRole[];
  isOnActivate: boolean;
  hasMultipleOrganizations: boolean;
  isOnExternalChooser: boolean;
  isAccountWhitelisted: boolean;
  appName: string;
}

export const generateAvatarHeroText = ({
  organizations,
  accountRoles,
  isOnActivate,
  hasMultipleOrganizations,
  isOnExternalChooser,
  isAccountWhitelisted,
  appName,
}: IGenerateAvatarHeroTextProps): string | null => {
  const currentProductCookie = detectProduct();
  const currentProduct = getProductName(currentProductCookie);
  const productDisplayName = getProductDisplayName(currentProduct);
  const hasNoProducts = organizations.every((organization) => !organization.products?.length);

  const checkPermission = (additionalScope = ''): boolean =>
    organizations.some((organization) =>
      accountRoles.find(
        (role) =>
          role.organization_id === organization.organization_id &&
          (role.scopes.includes('organization.product--my:rw') ||
            role.scopes.includes(additionalScope))
      )
    );

  const productIsInSomeOrganizations = (): boolean =>
    organizations.some((organization) =>
      organization.products.some((product) => product.name === currentProduct)
    );

  if (isOnActivate) {
    if (isAccountWhitelisted) {
      return 'Choose where you want to go.';
    }

    const productToActivate = Location.getHashParam('product') || currentProduct;

    if (hasMultipleOrganizations) {
      return `You are one click away from starting ${getProductDisplayName(productToActivate)}. Choose the organization.`;
    }
    return `You are one click away from starting ${getProductDisplayName(productToActivate)}.`;
  }
  if (isOnExternalChooser) {
    return `Where do you want to open ${appName === 'TextApp' ? 'Text App' : appName}?`;
  }
  if (
    (currentProduct === 'Undefined' || productIsInSomeOrganizations()) &&
    hasMultipleOrganizations
  ) {
    return 'Your account is linked with more organizations. Choose where you want to go.';
  }
  if (
    (currentProduct === 'Undefined' || productIsInSomeOrganizations()) &&
    !hasMultipleOrganizations
  ) {
    return 'Choose where you want to go.';
  }
  const additionalScope = currentProduct === 'Chatbot' ? 'organization.product.cb--my:rw' : '';
  if (checkPermission(additionalScope) && !productIsInSomeOrganizations() && !hasNoProducts) {
    return `You don’t have ${productDisplayName} yet, but you have other products on your account.`;
  }
  if (checkPermission(additionalScope) && !productIsInSomeOrganizations() && hasNoProducts) {
    return `You don’t have ${productDisplayName} yet.`;
  }
  if (!checkPermission(additionalScope) && !productIsInSomeOrganizations()) {
    return `You don’t have permissions to start a new trial. 
      Contact an admin if you need ${productDisplayName}.`;
  }
  return null;
};

export const getShouldDisplayExternalChooser = (
  originalClientId: string | undefined,
  products: Product[]
): boolean => {
  const currentProduct = detectProduct();

  // Force external chooser for Text App
  if (
    originalClientId === import.meta.env.VITE_APP_TA_CLIENT_ID ||
    currentProduct === ProductCookie.TextApp
  ) {
    return true;
  }

  return (
    originalClientId !== undefined &&
    originalClientId !== Config.default_client_id &&
    originalClientId !== import.meta.env.VITE_APP_CLIENT_ID &&
    !products.some((product) => product.client_id === originalClientId)
  );
};
